import { useEffect, useRef, useState, type FormEvent } from "react";
import {
  tipMoney,
  tipPaidEvent,
  tipUnitLimit,
  tipUnits,
  type TipSettings,
} from "../../../shared/src/tips";
import { Checkbox, Input, RadioGroup } from "./ui-kit/forms";
import "../../src/ui/tokens.css";
import "../../src/ui/forms.css";
import "../../src/ui/tip-box.css";

type Status =
  | "idle"
  | "sending"
  | "limited"
  | "unavailable"
  | "failed"
  | "checking"
  | "paid"
  | "example";

/**
 * A creator's Tip box, in authored pages, Tip sections and editor previews: a visitor picks how
 * many of the creator's "coffees" to buy, may leave a name and a message, and pays on the
 * creator's own Stripe Checkout (api/src/tips). It needs no account and works without
 * JavaScript. Coming back from checkout with ?tip= it asks whether the tip went through and
 * says thank you. A template's example takes choices like a real one but sends nothing.
 */
export function TipBox({
  id,
  heading,
  buttonLabel = "Support",
  settings,
  creatorName,
  picker = "counts",
  preview = false,
  example = false,
  pagePath = "/",
}: {
  id: string;
  heading: string;
  buttonLabel?: string;
  /** Round quick counts, or one count stepped down and up beside the price of one. */
  picker?: "counts" | "stepper";
  settings: TipSettings;
  creatorName: string;
  preview?: boolean;
  /** A template's example page: every choice works, and sending says it is an example. */
  example?: boolean;
  pagePath?: string;
}) {
  const anchor = `tip-${id}`;
  const limit = tipUnitLimit(settings);
  const presets = settings.presets.filter((count) => count <= limit);
  const stepper = picker === "stepper";
  const [units, setUnits] = useState(presets[0] ?? 1);
  // A stepper's count is always typed: it starts at the first quick count.
  const [custom, setCustom] = useState(stepper ? String(presets[0] ?? 1) : "");
  const [status, setStatus] = useState<Status>("idle");
  const customUnits = Math.floor(Number(custom));
  const count = custom && customUnits >= 1 ? Math.min(customUnits, limit) : units;
  const total = settings.unitCents * count;
  // The stepper's buttons write the typed count, which stays the field the form sends.
  const stepField = useRef<HTMLInputElement>(null);
  const step = (delta: number) => {
    const next = String(Math.min(limit, Math.max(1, count + delta)));
    setCustom(next);
    if (stepField.current) stepField.current.value = next;
  };

  // Back from checkout: ask a few times whether the tip went through.
  useEffect(() => {
    if (preview) return;
    const tip = new URL(location.href).searchParams.get("tip");
    if (!tip || !/^[0-9a-f-]{36}$/.test(tip)) return;
    let stopped = false;
    setStatus("checking");
    void (async () => {
      for (let attempt = 0; attempt < 6 && !stopped; attempt++) {
        const response = await fetch(`/api/tips/${tip}`).catch(() => null);
        const result = response?.ok
          ? ((await response.json().catch(() => ({}))) as { status?: string })
          : {};
        if (result.status === "paid") {
          if (stopped) return;
          setStatus("paid");
          // A Supporters element on the page shows the new tip at once.
          window.dispatchEvent(new Event(tipPaidEvent));
          return;
        }
        if (result.status !== "pending") break;
        await new Promise((resolve) => setTimeout(resolve, 1500));
      }
      if (!stopped) setStatus("idle");
    })();
    return () => {
      stopped = true;
    };
  }, [preview]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (preview || status === "sending") return;
    if (example) return setStatus("example");
    const fields = new FormData(event.currentTarget);
    setStatus("sending");
    try {
      const response = await fetch("/api/tips", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          units: count,
          name: fields.get("name"),
          message: fields.get("message"),
          private: fields.get("private") === "on",
          website: fields.get("website"),
          anchor,
          pagePath: location.pathname,
        }),
      });
      if (response.status === 429) return setStatus("limited");
      const result = (await response.json().catch(() => ({}))) as {
        status?: string;
        url?: string;
      };
      if (result.status === "unavailable") return setStatus("unavailable");
      if (!response.ok || result.status !== "checkout" || !result.url)
        throw new Error("Tip failed");
      location.assign(result.url);
    } catch {
      setStatus("failed");
    }
  }

  if (status === "paid")
    return (
      <div className="ui-tip" data-ui="tip" id={anchor} data-state="thanks">
        <p className="ui-tip-thanks-mark" aria-hidden="true">
          {settings.emoji || "♥"}
        </p>
        <h3 className="ui-tip-heading">Thank you!</h3>
        <p className="ui-tip-thanks" role="status">
          {settings.thanks}
        </p>
        <button
          className="button button-outline button-small"
          type="button"
          onClick={() => setStatus("idle")}
        >
          Support again
        </button>
      </div>
    );

  const sending = status === "sending" || status === "checking";
  return (
    <form
      action="/api/tips"
      method="post"
      className="ui-tip"
      data-ui="tip"
      id={anchor}
      onSubmit={(event) => void submit(event)}
      // An example page isn't hydrated; its controls script keeps the total and the answer.
      {...(example
        ? {
            "data-example": "",
            "data-unit-cents": settings.unitCents,
            "data-currency": settings.currency,
            "data-limit": limit,
            "data-label": buttonLabel,
          }
        : {})}
    >
      {heading && <h3 className="ui-tip-heading">{heading}</h3>}
      <fieldset disabled={preview || sending}>
        {stepper ? (
          <div className="ui-tip-units" data-picker="stepper">
            {settings.emoji && (
              <span className="ui-tip-emoji" aria-hidden="true">
                {settings.emoji}
              </span>
            )}
            <span className="ui-tip-each">
              {tipMoney(settings.unitCents, settings.currency)} each
            </span>
            <div className="ui-tip-stepper">
              <button
                className="ui-tip-step"
                type="button"
                data-step="-1"
                aria-label={`One ${settings.unit} fewer`}
                disabled={count <= 1}
                onClick={() => step(-1)}
              >
                −
              </button>
              <Input
                id={`${id}-custom`}
                name="custom"
                label={`How many ${settings.plural || `${settings.unit}s`}`}
                hideLabel
                className="ui-tip-custom"
                type="number"
                inputMode="numeric"
                min={1}
                max={limit}
                step={1}
                value={custom}
                ref={stepField}
                onChange={(event) => setCustom(event.currentTarget.value)}
              />
              <button
                className="ui-tip-step"
                type="button"
                data-step="1"
                aria-label={`One ${settings.unit} more`}
                disabled={count >= limit}
                onClick={() => step(1)}
              >
                +
              </button>
            </div>
          </div>
        ) : (
          <div className="ui-tip-units">
            {settings.emoji && (
              <span className="ui-tip-emoji" aria-hidden="true">
                {settings.emoji}
              </span>
            )}
            <span className="ui-tip-times" aria-hidden="true">
              ×
            </span>
            <RadioGroup
              name="units"
              label={`How many ${settings.plural || `${settings.unit}s`}`}
              hideLabel
              variant="segmented"
              className="ui-tip-presets"
              value={custom ? "" : String(units)}
              onChange={(value) => {
                setUnits(Number(value));
                setCustom("");
              }}
              options={presets.map((preset) => ({
                value: String(preset),
                label: String(preset),
                ariaLabel: tipUnits(settings, preset),
              }))}
            />
            <Input
              id={`${id}-custom`}
              name="custom"
              label={`Another number of ${settings.plural || `${settings.unit}s`}`}
              hideLabel
              className="ui-tip-custom"
              type="number"
              inputMode="numeric"
              min={1}
              max={limit}
              step={1}
              placeholder={String(Math.min(limit, Math.max(10, (presets.at(-1) ?? 5) * 2)))}
              onChange={(event) => setCustom(event.currentTarget.value)}
            />
          </div>
        )}
        {/* Both optional, as their placeholders say: an empty name shows as "Someone". */}
        <Input
          id={`${id}-name`}
          name="name"
          label="Your name (optional)"
          hideLabel
          autoComplete="name"
          maxLength={60}
          placeholder="Name or @yoursocial"
        />
        <Input
          id={`${id}-message`}
          name="message"
          label="Message (optional)"
          hideLabel
          multiline
          rows={3}
          maxLength={500}
          placeholder="Say something nice…"
        />
        <Checkbox id={`${id}-private`} name="private" label="Keep my message private" />
        <div hidden aria-hidden="true">
          <label>
            Website
            <input name="website" tabIndex={-1} autoComplete="off" />
          </label>
        </div>
        <input type="hidden" name="pagePath" value={pagePath} />
        <input type="hidden" name="anchor" value={anchor} />
        <button className="button button-primary ui-tip-submit" type="submit">
          {status === "sending"
            ? "Opening checkout…"
            : `${buttonLabel} ${tipMoney(total, settings.currency)}`}
        </button>
      </fieldset>
      <p role="status" aria-live="polite">
        {status === "checking"
          ? "Checking your tip…"
          : status === "limited"
            ? "That’s a lot of tips at once. Please try again in a little while."
            : status === "unavailable"
              ? `${creatorName} isn’t taking tips yet.`
              : status === "failed"
                ? "We couldn’t open checkout. Please try again."
                : status === "example"
                  ? "This is an example page, so no tip is taken."
                  : preview
                    ? "Preview: tips aren’t taken from here."
                    : ""}
      </p>
    </form>
  );
}
